import React from 'react';
import { Button, Popconfirm, Tooltip } from 'antd';
import Icon, { EditOutlined } from '@ant-design/icons';

import cloneSvg from '@icons/emp/clone.svg';
import deploySvg from '@icons/emp/deploy.svg';
import unDeploySvg from '@icons/emp/undeploy.svg';
import deleteSvg from '@icons/mapping-panel/delete-icon.svg';
import unblockSvg from '@icons/block-event/unblock.svg';

import { IActionConfirmButtonProps } from './typed';
import { hasRelatedFlows, hasRelatedMetrics } from '@src/emp-3.0/utils';
import { isObject } from 'lodash';

const actionTypeIcon = (style?: React.CSSProperties) => ({
  edit: <EditOutlined style={style} />,
  clone: <Icon component={cloneSvg} style={style} />,
  deploy: <Icon component={deploySvg} style={style} />,
  undeploy: <Icon component={unDeploySvg} style={style} />,
  delete: <Icon component={deleteSvg} style={style} />,
  unblock: <Icon component={unblockSvg} style={style} />,
});

export const ActionConfirmButton: React.FC<IActionConfirmButtonProps> = ({
  onConfirm,
  title,
  text,
  canAction,
  disabled,
  tooltip,
  style,
  button,
  ...restProps
}) => {
  const buttonIcon = actionTypeIcon(style)[text.toLowerCase()];
  const buttonStyle = canAction
    ? {
        border: '1px solid #d0021b',
        color: '#d0021b',
      }
    : {
        border: '1px solid #0046ff',
        color: '#0046ff',
      };

  const handleConfirm = () => {
    if (canAction) onConfirm?.();
  };

  const okText = canAction ? text : 'Got it';
  button =
    button ??
    (buttonIcon ? (
      <Tooltip title={tooltip ?? text} placement="topRight">
        <Button type="link" icon={buttonIcon} />
      </Tooltip>
    ) : (
      <Button type="link" style={{ padding: '3px 7px', color: 'rgb(0, 70, 255)', fontWeight: 600 }}>
        {text}
      </Button>
    ));

  return (
    <Popconfirm
      placement="bottomRight"
      title={title}
      onConfirm={handleConfirm}
      showCancel={canAction}
      okText={okText}
      cancelButtonProps={{ type: 'default' }}
      okButtonProps={{
        danger: canAction,
        type: 'ghost',
        style: buttonStyle,
      }}
      icon={false}
      disabled={disabled}
      {...restProps}
    >
      {button}
    </Popconfirm>
  );
};

// mapped event management
export const renderHasRelatedDeletePrompt = () => (
  <>
    <div style={{ whiteSpace: 'nowrap' }}>
      Unable to delete! Some metrics/flows are using it, please remove this
    </div>
    <div style={{ whiteSpace: 'nowrap' }}>event from related metrics/flows and try again.</div>
  </>
);

export const renderDeployedStatusDeletePrompt = () => (
  <>
    <div>Removes the item along with the related data. Deleted</div>
    <div> items cannot be restored. Proceed with Delete?</div>
  </>
);

export const renderDeployPrompt = () => (
  <>
    <div>Apply updates to full-scale data in DPI.</div>
    <div>Proceed with Deploy?</div>
  </>
);

export const renderUndeployPrompt = () => (
  <>
    <div>Deactivates the item so it cannot be selected or queried.</div>
    <div>Proceed with Undeploy?</div>
  </>
);

export const renderUndeployedStatusDeletePrompt = (type?: string) => (
  <>
    <div>The deleted {type} cannot be restored. Do you want to continue?</div>
  </>
);

export const renderRelatedList = (data) => {
  const relatedMetrics = data?.related_metrics ?? [];
  const relatedFlows = data?.related_flows ?? [];
  const isHasRelatedMetrics = hasRelatedMetrics(data);
  const isHasRelatedFlows = hasRelatedFlows(data);

  if (relatedMetrics.length === 0 && relatedFlows.length === 0) return null;
  return (
    <>
      {isHasRelatedMetrics ? <div>Related Metrics:</div> : null}
      <ul>
        {relatedMetrics.map((item: string | { id; name }, index) => {
          if (isObject(item)) return <li key={`${index}-${item.id}`}>{item.name}</li>;
          return <li key={`${index}-${item}`}>{item}</li>;
        })}
      </ul>
      {isHasRelatedFlows ? <div>Related Flows:</div> : null}
      <ul>
        {relatedFlows.map((item: string | { id; name }, index) => {
          if (isObject(item)) return <li key={`${index}-${item.id}`}>{item.name}</li>;
          return <li key={`${index}-${item}`}>{item}</li>;
        })}
      </ul>
    </>
  );
};

export const getRelatedConfirmTitle = (data: any, action = 'delete') => {
  return (
    <div>
      <div style={{ whiteSpace: 'nowrap' }}>
        Unable to {action}! Some metrics/flows are using it, please remove this
      </div>
      <div style={{ whiteSpace: 'nowrap' }}>event from related metrics/flows and try again.</div>
      {renderRelatedList(data)}
    </div>
  );
};
